import type { ReactNode } from "react";

import type {
  StarterCoordinationResponse,
  StarterRowResponse,
} from "../services/starterCoordination";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  rowKind,
  SOURCE_LABELS,
  STATUS_LABELS,
  statusHeading,
} from "./StarterCoordinationResultSummary";
import {
  CASE_LABELS,
  COORDINATION_TYPE_LABELS,
  FAULT_TYPE_LABELS,
  SCPD_KIND_LABELS,
  STARTING_METHOD_LABELS,
} from "./starterCoordinationDraft";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";

interface StarterCoordinationResultPanelProps {
  result: StarterCoordinationResponse;
}

/** Carried by every result (A20 (g)): what the checks are and are not. */
export const EVIDENCE_NOTE =
  "Coordination evidence is manufacturer-declared; this check does not prove coordination " +
  "(GAP-020).";

// The five words of the Result column. A check is true (passed), false (failed, a
// warning names it - or, for the test voltage, a review: IR-001 F1) or null: not made,
// either because its certificate item is not established or because the starting method
// is not DOL (A20 (e), (f)).
export const RESULT_WORDS = {
  pass: "Pass",
  fail: "Fails",
  review: "Review",
  notEvaluated: "Not evaluated",
  notEstablished: "Not established",
} as const;

/** The heading of a starter whose current checks were not made (IR-001 F3). */
export const PARTIALLY_CHECKED_LABEL = "Partially checked (current checks not evaluated)";

type NullMeaning = "notEvaluated" | "notEstablished";
type FalseMeaning = "fail" | "review";

function resultWord(check: boolean | null, whenNull: NullMeaning, whenFalse: FalseMeaning): string {
  if (check === null) {
    return RESULT_WORDS[whenNull];
  }
  return check ? RESULT_WORDS.pass : RESULT_WORDS[whenFalse];
}

function resultKey(check: boolean | null, whenNull: NullMeaning, whenFalse: FalseMeaning): string {
  if (check === null) {
    return whenNull === "notEvaluated" ? "not-evaluated" : "not-established";
  }
  return check ? "pass" : whenFalse;
}

// One cell holding an engineering figure: four significant figures with the unit on
// screen, the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null; unit: string }) {
  const quantity = formatQuantity(value, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

// The fault level every starter was held against, as the stored fault run holds it -
// or as typed, with no run, case or fault type (A20 (d)).
function FaultLevelTable({ result }: { result: StarterCoordinationResponse }) {
  const linked = result.current_source === "LINKED_FAULT_RUN";
  const current = formatQuantity(result.prospective_current_ka, "kA");
  const voltage = formatQuantity(result.system_nominal_voltage_v, "V");

  return (
    <ValueTable title="Fault level">
      <tr data-row-key="current_source">
        <th scope="row">Fault level source</th>
        <td data-current-source={result.current_source}>{SOURCE_LABELS[result.current_source]}</td>
      </tr>
      <tr data-row-key="fault_run_id">
        <th scope="row">Fault run</th>
        {/* The stored run's id: the evidence the fault level was read from. */}
        <td>{result.fault_run_id ?? EMPTY_QUANTITY}</td>
      </tr>
      <tr data-row-key="fault_bus_code">
        <th scope="row">Fault bus</th>
        <td>{result.fault_bus_code}</td>
      </tr>
      <tr data-row-key="bus_code">
        <th scope="row">Study bus</th>
        <td>{result.bus_code}</td>
      </tr>
      <tr data-row-key="calculation_case">
        <th scope="row">Calculation case</th>
        <td>
          {linked && result.calculation_case
            ? CASE_LABELS[result.calculation_case]
            : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="fault_type">
        <th scope="row">Fault type</th>
        <td>
          {linked && result.fault_type ? FAULT_TYPE_LABELS[result.fault_type] : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="prospective_current_ka">
        <th scope="row">Prospective current Ik'' (kA)</th>
        <td title={current.exact ?? undefined}>{current.display}</td>
      </tr>
      <tr data-row-key="system_nominal_voltage_v">
        <th scope="row">System nominal voltage (V)</th>
        <td title={voltage.exact ?? undefined}>{voltage.display}</td>
      </tr>
    </ValueTable>
  );
}

// One starter: each check beside the figure it was held against, then what else the
// row declared. Every figure is the engine's; nothing is compared or recomputed here.
function StarterTable({
  row,
  prospectiveCurrentKa,
  systemVoltageV,
}: {
  row: StarterRowResponse;
  prospectiveCurrentKa: string;
  systemVoltageV: string;
}) {
  const kind = rowKind(row);
  const title = `Starter ${row.motor_code} — ${
    kind === "partially-checked" ? PARTIALLY_CHECKED_LABEL : STATUS_LABELS[row.status]
  }`;
  const iq = formatQuantity(row.certified_iq_ka, "kA");
  const margin = formatQuantity(row.iq_margin_ka, "kA");
  const range = formatQuantity(row.overload_setting_min_a);
  const rangeMax = formatQuantity(row.overload_setting_max_a);
  const power = formatQuantity(row.rated_power_kw, "kW");
  const scpdRating = formatQuantity(row.scpd_rating_a, "A");

  const checks: {
    key: string;
    label: string;
    value: ReactNode;
    limit: ReactNode;
    check: boolean | null;
    whenNull: NullMeaning;
    whenFalse: FalseMeaning;
  }[] = [
    {
      key: "iq",
      label: "Iq against the fault level",
      value: <Quantity value={prospectiveCurrentKa} unit="kA" />,
      // The certified Iq with the margin Iq - Ik'' the engine reported beside it.
      limit: (
        <td title={iq.exact ?? undefined}>
          {row.certified_iq_ka === null
            ? EMPTY_QUANTITY
            : `${iq.display} · margin ${margin.display}`}
        </td>
      ),
      check: row.iq_adequate,
      whenNull: "notEstablished",
      whenFalse: "fail",
    },
    {
      key: "coordination_type",
      label: "Coordination type",
      value: <td>{COORDINATION_TYPE_LABELS[row.declared_coordination_type]} declared</td>,
      limit: <td>{COORDINATION_TYPE_LABELS[row.required_coordination_type]} required</td>,
      check: row.coordination_type_adequate,
      whenNull: "notEstablished",
      whenFalse: "fail",
    },
    {
      key: "test_voltage",
      label: "Test voltage Ue",
      value: <Quantity value={row.test_voltage_v} unit="V" />,
      limit: <Quantity value={systemVoltageV} unit="V" />,
      check: row.test_voltage_adequate,
      whenNull: "notEstablished",
      // Below the system voltage is a review, not a failure (IR-001 F1).
      whenFalse: "review",
    },
    {
      key: "contactor",
      label: "Contactor AC-3 rating",
      value: <Quantity value={row.contactor_ac3_rating_a} unit="A" />,
      limit: <Quantity value={row.full_load_current_a} unit="A" />,
      check: row.contactor_adequate,
      whenNull: "notEvaluated",
      whenFalse: "fail",
    },
    {
      key: "overload_range",
      label: "Overload setting range",
      value: (
        <td title={`${row.overload_setting_min_a}–${row.overload_setting_max_a} A`}>
          {range.display}–{rangeMax.display} A
        </td>
      ),
      limit: <Quantity value={row.full_load_current_a} unit="A" />,
      check: row.overload_range_adequate,
      whenNull: "notEvaluated",
      whenFalse: "fail",
    },
  ];

  return (
    <section
      aria-label={title}
      data-starter={row.motor_code}
      data-starter-status={row.status}
      data-starter-kind={kind}
    >
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Check</th>
              <th scope="col">Value</th>
              <th scope="col">Limit or requirement</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((item) => (
              <tr key={item.key} data-check-key={item.key}>
                <th scope="row">{item.label}</th>
                {item.value}
                {item.limit}
                <td data-check-result={resultKey(item.check, item.whenNull, item.whenFalse)}>
                  {/* The word in a pill (A25 (d)): the cell keeps the outcome attribute and the
                      text; the span is the styling hook of the pill alone. */}
                  <span data-check-pill>
                    <StatusIcon tone={checkTone(resultKey(item.check, item.whenNull, item.whenFalse))} size={16} />
                    {resultWord(item.check, item.whenNull, item.whenFalse)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* What else the row declared; none of it changes a check. */}
      <p data-starter-echo={row.motor_code}>
        <span title={power.exact ?? undefined}>{power.display}</span>
        {" · "}
        <span data-starting-method={row.starting_method}>
          {STARTING_METHOD_LABELS[row.starting_method]}
        </span>
        {" · "}
        <span title={scpdRating.exact ?? undefined}>
          {SCPD_KIND_LABELS[row.scpd_kind]} {scpdRating.display}
        </span>
        {" · Certificate "}
        <span data-certificate-reference="true">
          {row.certificate_reference ?? EMPTY_QUANTITY}
        </span>
      </p>
    </section>
  );
}

export function StarterCoordinationResultPanel({ result }: StarterCoordinationResultPanelProps) {
  return (
    <article aria-label="Starter coordination result">
      <header>
        <h2>Starter coordination result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status:{" "}
          <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <FaultLevelTable result={result} />

      {result.rows.map((row) => (
        <StarterTable
          key={row.motor_code}
          row={row}
          prospectiveCurrentKa={result.prospective_current_ka}
          systemVoltageV={result.system_nominal_voltage_v}
        />
      ))}

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The checks themselves carry
          no profile-dependent value; the profile is recorded with the run and must match the
          design basis of the project revision.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the linked fault study, every
          motor and starter rating, every coordination certificate and its test conditions
          before approval or issue. {EVIDENCE_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default StarterCoordinationResultPanel;
